import React, { useEffect, useState, useRef } from 'react';
import api from '../../api';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import { usePagination } from '../../hooks/usePagination';
import { useSelection } from '../../hooks/useSelection';
import { validateField } from '../../utils/validation';
import { useAuth } from '../../contexts/AuthProvider';
import { GenericView, SearchInput } from '../../components/common/table';

const Competitor = () => {
  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);

  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canCompetitor = (key) => can(key, 'competitor');

    // Pagination
    const pagination = usePagination(10);

    // Search
    const [search, setSearch] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

    const handleSearchInputChange = (value) => {
      setSearch(value);
      setSearchTerm(value);
    };

    const handleSearchSubmit = () => {
      setAppliedSearchTerm(searchTerm);
      pagination.setPage(0);
    };

    // Selection
    const {
        selectedIds,
        setSelectedIds,
        handleCheckboxClick,
        handleSelectAll
    } = useSelection();

    // Data and form state
    const [items, setItems] = useState([]);
    const [formData, setFormData] = useState({ name: '' });
    const [editingId, setEditingId] = useState(null);
    const [errors, setErrors] = useState({});
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showFailModal, setShowFailModal] = useState(false);
    const [failMessage, setFailMessage] = useState('');

    const fetchCompetitorList = async (searchValue = appliedSearchTerm) => {
        try {
            setIsLoading(true);
            let data = [];
            if (searchValue && searchValue.trim()) {
                const response = await api.get('/crm/lead-competitors/search', {
                    params: { name: searchValue.trim() }
                });
                data = response.data || [];
            } else {
                const response = await api.get('/crm/lead-competitors');
                data = response.data || [];
            }
            const total = data.length;
            const totalPages = Math.ceil(total / pagination.size) || 1;
            const start = pagination.page * pagination.size;
            const paginated = data.slice(start, start + pagination.size);
            setItems(paginated);
            pagination.setTotalPages(totalPages);
            pagination.setCurrentPageItems(paginated.length);
            pagination.setTotal(total);
        } catch (error) {
            console.error('Error fetching Competitor list:', error);
            let errorMessage = 'Failed to fetch Competitor list.';
            let technicalDetails = '';

            if (error.response) {
                switch (error.response.status) {
                    case 403:
                        errorMessage = 'You do not have permission to view Competitor.';
                        technicalDetails = 'Required role or permission is missing';
                        break;
                    case 500:
                        errorMessage = 'Server error while fetching Competitor.';
                        technicalDetails = error.response.data?.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while fetching Competitor.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred.';
                technicalDetails = error.message;
            }

            setItems([]);
            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCompetitorList();
    }, [pagination.page, pagination.size, appliedSearchTerm]);

    const handleCreate = () => {
    if (!canCompetitor('create')) { setFailMessage('You do not have permission to create.'); setShowFailModal(true); return; }
        setFormData({ name: '' });
        setEditingId(null);
        setErrors({});
        const modalEl = document.getElementById('modal-competitor');
        if (window.bootstrap && window.bootstrap.Modal && modalEl) {
            const modal = new window.bootstrap.Modal(modalEl);
            modal.show();
        }
    };

    const handleEdit = (item) => {
    if (!canCompetitor('edit')) { setFailMessage('You do not have permission to edit.'); setShowFailModal(true); return; }
        setFormData({ name: item.name || '' });
        setEditingId(item.id);
        setErrors({});
        setTimeout(() => {
            const modalEl = document.getElementById('modal-competitor');
            if (window.bootstrap && window.bootstrap.Modal && modalEl) {
                const modal = new window.bootstrap.Modal(modalEl);
                modal.show();
            }
        }, 100);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        const error = name === 'name' ? validateField('name', value, { required: true }) : null;
        setErrors((prev) => ({ ...prev, [name]: error }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const error = validateField('name', formData.name, { required: true });
        setErrors({ name: error });
        if (error) return;
        try {
            const payload = { name: formData.name.trim() };
            if (editingId) {
                await api.put(`/crm/lead-competitors/${editingId}`, payload);
                setSuccessMessage('Competitor has been successfully updated.');
            } else {
                await api.post('/crm/lead-competitors', payload);
                setSuccessMessage('Competitor has been successfully added.');
            }
            setShowSuccessModal(true);
            setEditingId(null);
            setFormData({ name: '' });
            setErrors({});
            await fetchCompetitorList();
            const modalEl = document.getElementById('modal-competitor');
            const bootstrapModal = window.bootstrap?.Modal.getInstance(modalEl);
            if (bootstrapModal) bootstrapModal.hide();
        } catch (error) {
            console.error('Error saving Competitor:', error);
            let errorMessage = 'Failed to save Competitor.';
            let technicalDetails = '';

            if (error.response) {
                switch (error.response.status) {
                    case 409:
                        errorMessage = 'A Competitor with this name already exists.';
                        technicalDetails = `Duplicate entry for name: "${formData.name}"`;
                        break;
                    case 400:
                        errorMessage = 'Invalid data provided.';
                        technicalDetails = error.response.data?.message || 'The server rejected the request due to invalid data';
                        break;
                    case 403:
                        errorMessage = 'You do not have permission to perform this action.';
                        technicalDetails = 'Required role or permission is missing for this operation';
                        break;
                    case 404:
                        errorMessage = 'Competitor not found.';
                        technicalDetails = 'The requested item may have been deleted or does not exist';
                        break;
                    case 500:
                        errorMessage = 'Server encountered an error while processing the request.';
                        technicalDetails = error.response.data?.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while saving Competitor.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred.';
                technicalDetails = error.message;
            }

            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
        }
    };

    const editSelected = () => {
        if (Array.isArray(selectedIds) && selectedIds.length !== 1) {
            setSuccessMessage('Please select exactly one item to edit');
            setShowSuccessModal(true);
            return;
        }
        const selectedId = Array.isArray(selectedIds) ? selectedIds[0] : null;
        const selectedItem = Array.isArray(items) && items.find((item) => item.id === selectedId);
        if (selectedItem) handleEdit(selectedItem);
    };

    const deleteSelected = () => {
    if (!canCompetitor('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
        if (Array.isArray(selectedIds) && selectedIds.length === 0) {
            setFailMessage('Please select at least one Competitor to delete.\n\nTechnical Details: No items selected for deletion');
            setShowFailModal(true);
            return;
        }
        setConfirmAction(() => async () => {
            try {
                await Promise.all(selectedIds.map((id) => api.delete(`/crm/lead-competitors/${id}`)));
                setSelectedIds([]);
                fetchCompetitorList();
                setShowDeleteConfirmation(false);
                setSuccessMessage('Selected Competitor items deleted successfully');
                setShowSuccessModal(true);
            } catch (error) {
                console.error('Error deleting Competitor:', error);
                let errorMessage = 'Failed to delete Competitor.';
                let technicalDetails = '';

                if (error.response) {
                    switch (error.response.status) {
                        case 403:
                            errorMessage = 'You do not have permission to delete these items.';
                            technicalDetails = 'Required role or permission is missing for deletion';
                            break;
                        case 400:
                            errorMessage = 'Invalid delete request.';
                            technicalDetails = error.response.data?.message || 'The server rejected the deletion request';
                            break;
                        case 404:
                            errorMessage = 'One or more items not found.';
                            technicalDetails = 'Some items may have been already deleted';
                            break;
                        case 405:
                            errorMessage = 'Delete operation not allowed.';
                            technicalDetails = 'The server does not support the DELETE method for this endpoint';
                            break;
                        case 500:
                            errorMessage = 'Server error while deleting items.';
                            technicalDetails = error.response.data?.message || 'Internal server error during deletion';
                            break;
                        default:
                            errorMessage = 'An error occurred while deleting the items.';
                            technicalDetails = `Server responded with status: ${error.response.status}`;
                    }
                } else if (error.request) {
                    errorMessage = 'Unable to connect to the server.';
                    technicalDetails = 'Network error or server is not responding';
                } else {
                    errorMessage = 'An unexpected error occurred during deletion.';
                    technicalDetails = error.message;
                }

                setShowDeleteConfirmation(false);
                setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
                setShowFailModal(true);
            }
        });
        setShowDeleteConfirmation(true);
    };

    useEffect(() => {
        const modal = document.getElementById('modal-competitor');
        const onHidden = () => {
            setFormData({ name: '' });
            setEditingId(null);
        };
        modal?.addEventListener('hidden.bs.modal', onHidden);
        return () => modal?.removeEventListener('hidden.bs.modal', onHidden);
    }, []);

    const formatDate = (val) => (val ? new Date(val).toLocaleString() : '');

    return (
        <>
            {/* Page header */}
            <div className="page-header d-print-none">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col">
                            <h2 className="page-title d-flex align-items-center">
                                Competitor
                            </h2>
                        </div>
                        <div className="col-auto ms-auto d-print-none">
                            <div className="btn-list">
                                <a href="#" className="btn btn-primary btn-5 d-none d-sm-inline-block" onClick={handleCreate}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="icon icon-2">
                                        <path d="M12 5l0 14" />
                                        <path d="M5 12l14 0" />
                                    </svg>
                                    Create
                                </a>
                                <a href="#" className="btn btn-primary btn-6 d-sm-none btn-icon" onClick={handleCreate} aria-label="Create new competitor">
                                    <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="icon icon-2">
                                        <path d="M12 5l0 14" />
                                        <path d="M5 12l14 0" />
                                    </svg>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">
                    <div className="col-12">
                        <div className="card">
                          <div className="card-body">
                            <GenericView
                              data={items}
                              columns={[
                                { key: 'name', label: 'Name', sortable: true },
                                { key: 'createdAt', label: 'Created Date', sortable: true, render: (value, row) => formatDate(row.createdAt || row.cdate || row.created_at) },
                                { key: 'updatedAt', label: 'Updated Date', sortable: true, render: (value, row) => formatDate(row.updatedAt || row.udate || row.updated_at) },
                              ]}
                              isLoading={isLoading || capabilitiesLoading}
                              emptyStateMessage="No competitors found"
                              checkboxSelection={true}
                              selectedRows={selectedIds}
                              onSelectRow={(itemId, isSelected) => {
                                if (isSelected) {
                                  setSelectedIds([...selectedIds, itemId]);
                                } else {
                                  setSelectedIds(selectedIds.filter(id => id !== itemId));
                                }
                              }}
                              onSelectAllRows={(itemIds) => {
                                if (Array.isArray(itemIds) && itemIds.length > 0) {
                                  setSelectedIds([...new Set([...selectedIds, ...itemIds])]);
                                } else {
                                  setSelectedIds([]);
                                }
                              }}
                              onEdit={canCompetitor('edit') ? (item) => {
                                setSelectedIds([item.id]);
                                handleEdit(item);
                              } : undefined}
                              onDelete={canCompetitor('delete') ? (item) => {
                                setSelectedIds([item.id]);
                                deleteSelected();
                              } : undefined}
                              searchComponent={
                                <div className="d-flex gap-2">
                                  <SearchInput
                                    value={searchTerm}
                                    onChange={handleSearchInputChange}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSearchSubmit();
                                      }
                                    }}
                                    placeholder="Search..."
                                    uniqueId="competitor-search"
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSearchSubmit}
                                  >
                                    Search
                                  </button>
                                </div>
                              }
                              actionButtons={
                                <div className="d-flex gap-2">
                                  {(canCompetitor('edit') || canCompetitor('delete')) && (
                                    <div ref={actionsContainerRef} className="dropdown">
                                      <a
                                        ref={actionsToggleRef}
                                        href="#"
                                        className="btn dropdown-toggle"
                                        data-bs-toggle="dropdown"
                                        aria-expanded={isActionsOpen}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          const inst = actionsDropdownInstanceRef.current;
                                          if (inst?.toggle) inst.toggle();
                                          else setIsActionsOpen(prev => !prev);
                                        }}
                                      >Actions</a>
                                      <div className="dropdown-menu">
                                        {canCompetitor('edit') && (
                                          <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); editSelected(); }}
                                            style={{ color: selectedIds.length === 1 ? "" : "#ccc", pointerEvents: selectedIds.length === 1 ? "auto" : "none" }}>
                                            Edit
                                          </a>
                                        )}
                                        {canCompetitor('delete') && (
                                          <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); deleteSelected(); }}
                                            style={{ color: selectedIds.length ? "" : "#ccc", pointerEvents: selectedIds.length ? "auto" : "none" }}>
                                            Delete
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              }
                              pagination={{
                                currentPage: pagination.page + 1,
                                totalRows: pagination.total,
                                rowsPerPage: pagination.size,
                                onPageChange: (page) => pagination.setPage(page - 1),
                                onRowsPerPageChange: (size) => {
                                  pagination.setSize(size);
                                  pagination.setPage(0);
                                }
                              }}
                              pageSizeOptions={[10, 25, 50, 100]}
                              uniqueId="competitor-table"
                            />
                          </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal modal-blur fade" id="modal-competitor" tabIndex="-1" role="dialog" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{editingId ? 'Edit Competitor' : 'Create Competitor'}</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-lg-12">
                                        <div className="mb-3">
                                            <label className="form-label required">Name</label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                placeholder="Enter competitor name"
                                            />
                                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <a href="#" className="btn btn-link link-secondary btn-3" data-bs-dismiss="modal">Cancel</a>
                                <button type="submit" className="btn btn-primary ms-auto">
                                    {editingId ? 'Update' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <ConfirmationModal
                isOpen={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={confirmAction || (() => {})}
                title="Delete Confirmation"
                message={Array.isArray(selectedIds) && selectedIds.length > 0
                    ? 'Are you sure you want to delete the selected Competitor items? This action cannot be undone.'
                    : 'Are you sure you want to delete this Competitor? This action cannot be undone.'}
                confirmText="Delete"
                cancelText="Cancel"
                type="danger"
            />
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                title="Success"
                message={successMessage}
                buttonText="OK"
            />
            <FailModal
                isOpen={showFailModal}
                onClose={() => setShowFailModal(false)}
                title="Error"
                message={failMessage}
                buttonText="OK"
            />
        </>
    );
};

export default Competitor;
